import { LitElement, css, html } from 'lit';
import { customElement, property, query } from 'lit/decorators';
import { isDefined, isValidString } from '../../helpers/utils';

import { StateController } from '../../helpers/state/state-management/state-controller';
import { TranslatedSpan } from '../theme/translated-span';
import { ensureHTTPS } from '../../helpers/urls';
import featuresInfo from '../../helpers/state/features-info';
import { getVisibleTextColor } from '../../helpers/colors';
import { palette } from '../../style/variables';
import { storeReferralFromUrl } from '../../helpers/api/referral-api';
import topicData from '../../helpers/state/topic-data';

@customElement('map-header')
export class MapHeader extends LitElement {
    static amplitude_callsite: string = 'Map Header';

    @property({ type: String })
    textOverride?: string;

    @query('#name-span')
    nameSpan!: TranslatedSpan;

    @query('#creator-span')
    creatorSpan!: TranslatedSpan;

    private readonly bindTopicState = new StateController(this, topicData);

    private async onClickOutParams() {
        if (isValidString(topicData.topic?.header_url) && isDefined(topicData.topic?.header_url) && featuresInfo.headerLinkOutEnabled) {
            window.open(ensureHTTPS(topicData.topic?.header_url), '_blank', 'noopener,noreferrer');
        } else {
            let url = `https://www.proxi.co/?utm_source=embed&utm_medium=referral&utm_content=map_header&utm_term=${topicData.topicId ?? ''}`;
            // Add the map owner's referral code if one exists
            const refCode = topicData.organizationPassiveReferralCode;
            if (refCode) {
                url += `&ref=${encodeURIComponent(refCode)}`;
            }

            await storeReferralFromUrl(url);
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    }

    async refreshText() {
        await this.nameSpan.refreshText();
        await this.creatorSpan.refreshText();
    }

    render() {
        const headerColor = topicData.backgroundColor;
        const fontFamily = topicData.topic?.custom_font ?? 'Montserrat';
        const logoFile = (getVisibleTextColor(headerColor) === palette.primaryBlue) ? 'logo_black.png' : 'logo_white.png';
        const logo = topicData.topic?.header_logo === '/images/logo.png' || !featuresInfo.headerLogoEnabled ? `/images/${logoFile}` : topicData.topic?.header_logo ?? `/images/${logoFile}`;
        const headertextcolor = topicData.textColor;
        const headerSize = topicData.topic?.mobile_header_size;
        const hasCustomHeaderSize = isDefined(headerSize);
        const headerScale = hasCustomHeaderSize ? Math.max(0.75, Math.min((headerSize) / 32, 2.5)) : 1;
        const logoStyle = hasCustomHeaderSize ? `max-height: ${headerSize}px;` : '';
        const headerContentStyle = hasCustomHeaderSize
            ? `color: ${headertextcolor}; padding: ${Math.round(5 * headerScale)}px;`
            : `color: ${headertextcolor};`;
        const dividerStyle = hasCustomHeaderSize ? `height: ${(1.5 * headerScale).toFixed(2)}rem;` : '';
        const titleStyle = hasCustomHeaderSize
            ? `color: ${headertextcolor}; font-size: ${(1 * headerScale).toFixed(2)}em;`
            : `color: ${headertextcolor};`;
        const creatorStyle = hasCustomHeaderSize
            ? `font-size: ${(0.9 * headerScale).toFixed(2)}em;`
            : 'font-size: .9em;';
        return html`
            ${topicData.topic?.hide_header === true ? '' :
            html`
            <div id="header" style="background-color: ${headerColor}; font-family: ${fontFamily}">
                <div id="headerContent" style="${headerContentStyle}">
                    <div class="headerPart">
                        <a
                            id="logo"
                            @click="${async () => await this.onClickOutParams()}"
                            target="_blank"
                            class="__web-inspector-hide-shortcut__"
                        >
                            <img src="${logo}" class="logoImg" style="${logoStyle}" />
                        </a>
                        <div class="headerDivider" style="${dividerStyle}"></div>
                        <span
                            id="title"
                            style="${titleStyle}"
                        >
                            ${topicData.name !== undefined && this.textOverride === undefined
                                ? html`
                                      <b><translated-span id="name-span">${topicData?.topic?.name}</translated-span></b><br />
                                      ${topicData.topic?.creator !== undefined
                                          ? html`<translated-span id="creator-span" style="${creatorStyle}" class="for-translation"
                                                >${topicData.topic?.creator}</translated-span
                                            >`
                                          : ''}
                                  `
                                : html`
                                   <b>${this.textOverride}</b>
                                    `}
                        </span>
                    </div>
                    <slot></slot>
                </div>
            </div>
            `}
        `;
    }

    static get styles() {
        return css`
            #header {
                width: 100%;
                margin: 0px;
            }

            #headerContent {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                padding: 5px;
            }

            .headerPart {
                display: flex;
                align-items: center;
            }

            .headerDivider {
                width: 1px;
                height: 1.5rem;
                margin: 0 0.75rem;
                background-color: var(--main-moon-color);
            }

            #logo {
                max-width: 40%;
                display: flex;
                cursor: pointer;
            }

            .logoImg {
                max-height: 2.5rem;
                max-width: 100%;
            }

            #title {
                position: relative;
            }

            @media only screen and (max-width: 550px) {
                .logoImg {
                    max-height: 2rem;
                }
            }
        `;
    }
}
